import type { BoxProps, ImageProps as ChakraImageProps } from '@chakra-ui/react';
import { Image as ChakraImage } from '@chakra-ui/react';
import React from 'react';

import { Skeleton } from './skeleton';

export interface ImageProps extends ChakraImageProps {
  fallback?: React.ReactNode;
  // for the case where the image dimensions are not known before the image is loaded
  skeletonWidth?: BoxProps['width'];
  skeletonHeight?: BoxProps['height'];
}

export const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  function Image(props, ref) {
    const { fallback, src, onLoad, onError, skeletonWidth, skeletonHeight, alt, ...rest } = props;

    const [ loading, setLoading ] = React.useState(true);
    const [ error, setError ] = React.useState(false);
    const imgRef = React.useRef<HTMLImageElement>(null);

    // If the image loaded before React could attach the onLoad handler (e.g. from browser cache),
    // the event is already fired and will never fire again. Check img.complete on mount.
    React.useEffect(() => {
      const img = imgRef.current;
      if (img && img.complete) {
        if (img.naturalWidth > 0) {
          setLoading(false);
        } else {
          setError(true);
          setLoading(false);
        }
      }
    }, [ src ]);

    const mergedRef = React.useCallback((node: HTMLImageElement | null) => {
      (imgRef as React.MutableRefObject<HTMLImageElement | null>).current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLImageElement | null>).current = node;
      }
    }, [ ref ]);

    const handleLoadError = React.useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
      setError(true);
      setLoading(false);
      onError?.(event);
    }, [ onError ]);

    const handleLoadSuccess = React.useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
      setLoading(false);
      onLoad?.(event);
    }, [ onLoad ]);

    if (!src && fallback) {
      if (React.isValidElement(fallback)) {
        return React.cloneElement(fallback, rest);
      }
      return fallback;
    }

    if (error) {
      if (React.isValidElement(fallback)) {
        return React.cloneElement(fallback, rest);
      }
      return fallback;
    }

    const skeletonProps: BoxProps = {
      ...rest as BoxProps,
      ...(skeletonWidth !== undefined && { width: skeletonWidth }),
      ...(skeletonHeight !== undefined && { height: skeletonHeight }),
    };

    return (
      <>
        { loading && <Skeleton loading { ...skeletonProps }/> }
        <ChakraImage
          ref={ mergedRef }
          src={ src }
          alt={ alt }
          onError={ handleLoadError }
          onLoad={ handleLoadSuccess }
          { ...rest }
          display={ loading ? 'none' : rest.display || 'block' }
        />
      </>
    );
  },
);
